import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { useTour } from '../../contexts/TourContext';
import { useAuth } from '../../contexts/AuthContext';
import { useBranding } from '../../contexts/BrandingContext';
import {
  Rocket,
  BookOpen,
  ArrowRight,
  LayoutDashboard,
  ShoppingCart,
  CheckCircle,
  Users,
  Map,
} from 'lucide-react';

export function WelcomeModal() {
  const { showWelcome, dismissWelcome, startTour, setShowQuickReference } = useTour();
  const { user } = useAuth();
  const { branding } = useBranding();

  const isSupplier = user?.platform === 'SUPPLIER';
  const portalName = branding.companyName?.trim() || 'Supplier Portal';

  const handleStartTour = () => {
    dismissWelcome();
    startTour();
  };

  const handleShowReference = () => {
    dismissWelcome();
    setShowQuickReference(true);
  };

  const handleSkip = () => {
    dismissWelcome();
  };

  return (
    <Dialog open={showWelcome} onOpenChange={(open) => !open && dismissWelcome()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="p-3 bg-primary/10 rounded-full">
              <Rocket className="h-6 w-6 text-primary" />
            </div>
            <div>
              <DialogTitle className="text-xl">Welcome to {portalName}!</DialogTitle>
              <DialogDescription className="text-sm">
                {user?.first_name ? `Hi ${user.first_name}, ` : ''}Let's get you started
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <p className="text-sm text-muted-foreground">
            {isSupplier
              ? "Welcome to your new supplier portal. Here's what you can do:"
              : `Welcome to the ${portalName} Supplier Management Platform. Here's what's available:`}
          </p>

          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 bg-slate-50 rounded-lg border">
              <LayoutDashboard className="h-5 w-5 text-blue-600 mb-2" />
              <h4 className="font-medium text-sm">Dashboard</h4>
              <p className="text-xs text-muted-foreground">View KPIs and charts</p>
            </div>
            <div className="p-3 bg-slate-50 rounded-lg border">
              <ShoppingCart className="h-5 w-5 text-purple-600 mb-2" />
              <h4 className="font-medium text-sm">Purchasing</h4>
              <p className="text-xs text-muted-foreground">
                {isSupplier ? 'View your POs' : 'Manage orders'}
              </p>
            </div>
            <div className="p-3 bg-slate-50 rounded-lg border">
              <CheckCircle className="h-5 w-5 text-green-600 mb-2" />
              <h4 className="font-medium text-sm">Quality</h4>
              <p className="text-xs text-muted-foreground">Track NCRs</p>
            </div>
            <div className="p-3 bg-slate-50 rounded-lg border">
              <Users className="h-5 w-5 text-amber-600 mb-2" />
              <h4 className="font-medium text-sm">{isSupplier ? 'Profile' : 'Suppliers'}</h4>
              <p className="text-xs text-muted-foreground">
                {isSupplier ? 'Your company info' : 'Manage suppliers'}
              </p>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <h4 className="font-medium text-sm text-blue-900 flex items-center gap-2">
              <Map className="h-4 w-4" />
              New Navigation
            </h4>
            <p className="text-xs text-blue-800 mt-1">
              The main menu is now on the <strong>left sidebar</strong> instead of the top.
              Click the arrow to collapse/expand it.
            </p>
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={handleSkip} className="sm:mr-auto">
            Skip for now
          </Button>
          <Button variant="outline" onClick={handleShowReference}>
            <BookOpen className="h-4 w-4 mr-2" />
            Quick Reference
          </Button>
          <Button onClick={handleStartTour}>
            <Rocket className="h-4 w-4 mr-2" />
            Take a Tour
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
